import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api, Job, Application } from '../../lib/api';
import { PostJobForm } from './PostJobForm';
import { JobDetailsModal } from './JobDetailsModal';
import { getRecommendationsForGC, RecommendationScore } from '../../lib/recommendations';
import { TradeBadge } from '../shared/TradeBadge';
import {
  Briefcase,
  Users,
  AlertTriangle,
  TrendingUp,
  Plus,
  Clock,
  MessageSquare,
  Eye,
  FileText,
  Sparkles,
  Star,
  MapPin
} from 'lucide-react';

interface GCDashboardProps {
  onViewChange?: (view: string) => void;
}

export const GCDashboard: React.FC<GCDashboardProps> = ({ onViewChange }) => {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    activeJobs: 0,
    verifiedTrades: 0,
    complianceRisk: 0,
    responseRate: 0,
  });
  const [recentJobs, setRecentJobs] = useState<(Job & { applications_count?: number })[]>([]);
  const [recentApplications, setRecentApplications] = useState<Application[]>([]);
  const [recommendations, setRecommendations] = useState<RecommendationScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPostJobForm, setShowPostJobForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, [profile]);

  const loadDashboardData = async () => {
    if (!profile) return;

    try {
      const [jobs, applications] = await Promise.all([
        api.get<Job[]>('/jobs', { gc_id: profile.id, limit: 5 }),
        api.get<Application[]>('/applications', { gc_id: profile.id, limit: 10 })
      ]);

      const jobsList = jobs || [];
      const applicationsList = applications || [];

      const activeJobs = jobsList.filter(j => j.status === 'open').length;

      // Get application counts for each job
      for (const job of jobsList) {
        try {
          const jobApplications = await api.get<Application[]>('/applications', { job_id: job.id });
          job.applications_count = jobApplications?.length || 0;
        } catch (error) {
          job.applications_count = 0;
        }
      }

      setStats({
        activeJobs,
        verifiedTrades: Math.floor(Math.random() * 15) + 5,
        complianceRisk: Math.floor(Math.random() * 5),
        responseRate: 87,
      });

      setRecentJobs(jobsList);
      setRecentApplications(applicationsList);

      if (profile?.id && activeJobs > 0) {
        const recs = await getRecommendationsForGC(profile.id, 3);
        setRecommendations(recs);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-50 text-green-700';
      case 'filled':
        return 'bg-amber-50 text-amber-700';
      case 'closed':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getApplicationStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-amber-50 text-amber-700';
      case 'contacted':
        return 'bg-amber-50 text-amber-700';
      case 'verified':
        return 'bg-green-50 text-green-700';
      case 'hired':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-slate-100 text-slate-600';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  const handleJobPosted = () => {
    setShowPostJobForm(false);
    loadDashboardData();
  };

  return (
    <>
      {showPostJobForm && (
        <PostJobForm
          onClose={() => setShowPostJobForm(false)}
          onSuccess={handleJobPosted}
        />
      )}

      <div className="space-y-6">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-8 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="relative">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">GC Dashboard</h1>
                <p className="text-slate-300">Manage your jobs and find qualified trades</p>
              </div>
              <button
                onClick={() => setShowPostJobForm(true)}
                className="post-job-button flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Job
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
                <div className="text-xs text-slate-300 mb-1">Active Jobs</div>
                <div className="text-2xl font-bold text-white">{stats.activeJobs}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
                <div className="text-xs text-slate-300 mb-1">Verified Trades</div>
                <div className="text-2xl font-bold text-white">{stats.verifiedTrades}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
                <div className="text-xs text-slate-300 mb-1">Compliance</div>
                <div className="text-2xl font-bold text-white">{stats.complianceRisk}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
                <div className="text-xs text-slate-300 mb-1">Response Rate</div>
                <div className="text-2xl font-bold text-white">{stats.responseRate}%</div>
              </div>
            </div>
          </div>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-amber-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-md">
              <Briefcase className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.activeJobs}</div>
          <div className="text-sm text-slate-600 font-medium">Active Jobs</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full" style={{width: '75%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-green-50 rounded-xl border border-green-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.verifiedTrades}</div>
          <div className="text-sm text-slate-600 font-medium">Verified Trades</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" style={{width: '90%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-amber-50 rounded-xl border border-amber-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.complianceRisk}</div>
          <div className="text-sm text-slate-600 font-medium">Compliance Alerts</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-red-500 to-orange-600 rounded-full" style={{width: '25%'}}></div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">{stats.responseRate}%</div>
          <div className="text-sm text-slate-600 font-medium">Response Rate</div>
          <div className="mt-3 h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-slate-600 to-slate-700 rounded-full" style={{width: `${stats.responseRate}%`}}></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">Quick Actions</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowPostJobForm(true)}
              className="post-job-button bg-gradient-to-br from-white to-amber-50 border-2 border-amber-200 hover:border-amber-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Post Job</div>
              <div className="text-sm text-slate-600 mt-1">Create new listing</div>
            </button>

            <button
              onClick={() => onViewChange?.('manage-jobs')}
              className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-amber-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Manage Jobs</div>
              <div className="text-sm text-slate-600 mt-1">View all listings</div>
            </button>

            <button
              onClick={() => onViewChange?.('trades')}
              className="find-contractors bg-gradient-to-br from-white to-amber-50 border-2 border-amber-200 hover:border-amber-500 hover:shadow-xl rounded-xl p-6 text-left transition-all group hover:scale-[1.02]"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="font-bold text-slate-900">Find Trades</div>
              <div className="text-sm text-slate-600 mt-1">Browse contractors</div>
            </button>
          </div>

          <div className="active-jobs bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-amber-600" />
              Recent Jobs
            </h3>
            {recentJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm">No jobs posted yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="group flex items-center justify-between pb-3 border-b border-slate-100 last:border-0 hover:bg-amber-50 -mx-3 px-3 py-2 rounded-lg transition-all cursor-pointer hover:shadow-sm"
                    onClick={() => setSelectedJob(job)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900 truncate">{job.title}</div>
                        <Eye className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className="text-xs text-slate-600">{job.trade_type}</span>
                        <span className="text-xs text-slate-400">•</span>
                        <span className="text-xs text-slate-600">{job.applications_count || 0} applications</span>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(job.status)} flex-shrink-0`}>
                      {job.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-amber-600" />
                Recent Activity
              </h3>
            </div>

            {recentApplications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p className="text-sm">No applications yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentApplications.slice(0, 5).map((app) => (
                  <div key={app.id} className="pb-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-sm text-slate-900">New Application</div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getApplicationStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600">
                      {new Date(app.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {recommendations.length > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-slate-50 rounded-xl border border-amber-200 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-slate-900">Recommended for You</h3>
              </div>
              <div className="space-y-3">
                {recommendations.map(({ contractor }) => (
                  <div key={contractor.id} className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-slate-700">
                          {contractor.full_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-900 text-sm truncate">{contractor.full_name}</h4>
                        <div className="flex flex-wrap gap-1 mt-1 mb-2">
                          {contractor.trades && contractor.trades.length > 0 && (
                            <TradeBadge trade={contractor.trades[0]} size="sm" />
                          )}
                        </div>
                        <div className="flex items-center text-xs text-slate-600 space-x-2">
                          <div className="flex items-center">
                            <Star className="w-3 h-3 mr-0.5 text-amber-500" />
                            {contractor.rating.toFixed(1)}
                          </div>
                          <span>•</span>
                          <span>{contractor.jobs_completed} jobs</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-600 mt-3">
                Based on your open jobs
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
      {selectedJob && (
        <JobDetailsModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onJobUpdated={loadDashboardData}
          onJobDeleted={() => {
            setSelectedJob(null);
            loadDashboardData();
          }}
        />
      )}
    </>
  );
};
